import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useNutritionContext } from '../../hooks/useNutritionContext';
import { useAuth } from '../../context/AuthContext';
import { getCentralDate } from '../../utils/dateUtils';
import { isIosApp } from '../../utils/appPlatform';

import {
  HomeIcon,
  BookOpenIcon,
  ArchiveBoxIcon,
  BeakerIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ShoppingBagIcon,
  CalculatorIcon,
  ChatBubbleLeftRightIcon,
  BookmarkIcon,
  EnvelopeIcon,
  BoltIcon
} from '@heroicons/react/24/outline';

const Sidebar = ({ isOpen, isMobile }) => {
  const { state } = useNutritionContext();
  const { user } = useAuth();
  const location = useLocation();
  const iosApp = isIosApp();

  // Check if user has pro access (KDP users or paid subscribers)
  const hasPro = user?.subscriptionStatus === 'pro' || user?.subscriptionStatus === 'premium';

  const allNavItems = [
    { path: '/dashboard', icon: HomeIcon, label: 'Dashboard' },
    { path: '/tracker', icon: BookOpenIcon, label: 'Food Tracker' },
    { path: '/nutrition-coach', icon: ChatBubbleLeftRightIcon, label: 'AI Nutrition Coach' },
    { path: '/saved-recipes', icon: BookmarkIcon, label: 'Recipes' },
    { path: '/macro-calculator', icon: CalculatorIcon, label: 'Macro Calculator' },
    { path: '/macro-snacks', icon: ShoppingBagIcon, label: 'Macro Snacks' },
    { path: '/progress-reports', icon: ChartBarIcon, label: 'Progress Reports' },
    { path: '/grocery', icon: ShoppingBagIcon, label: 'Grocery List' },
    { path: '/contact', icon: EnvelopeIcon, label: 'Contact Us' },
    { path: '/upgrade', icon: BoltIcon, label: 'Upgrade to Pro', highlight: true }
  ];

  // Filter out upgrade button if user already has pro
  const navItems = allNavItems.filter((item) => {
    if (iosApp && ['/macro-snacks', '/contact'].includes(item.path)) return false;
    if (item.path === '/upgrade' && hasPro) return false;
    return true;
  });

  const isActive = (path) => location.pathname === path;

  const NavItem = ({ item }) => {
    const Icon = item.icon;
    const active = isActive(item.path);

    if (item.highlight) {
      return (
        <Link
          to={item.path}
          className="flex items-center px-4 py-2.5 mx-2 my-0.5 text-sm font-semibold rounded-xl transition-all bg-gradient-to-r from-indigo-500 to-violet-500 text-white hover:from-indigo-600 hover:to-violet-600 shadow-sm shadow-indigo-200 dark:shadow-indigo-900/30"
        >
          <Icon className="w-4 h-4 mr-3 flex-shrink-0" />
          <span>{item.label}</span>
        </Link>
      );
    }

    return (
      <Link
        to={item.path}
        className={`relative flex items-center px-4 py-2.5 mx-2 my-0.5 text-sm font-medium rounded-xl transition-all duration-150 ${
          active
            ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700/60 dark:hover:text-gray-100'
        }`}
      >
        {active && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-indigo-500 rounded-r-full" />
        )}
        <Icon className={`w-4 h-4 mr-3 flex-shrink-0 ${active ? 'text-indigo-500' : ''}`} />
        <span>{item.label}</span>
      </Link>
    );
  };

  const ProgressBar = ({ value, goal, color }) => {
    const pct = goal > 0 ? Math.min((value / goal) * 100, 100) : 0;
    const over = goal > 0 && value > goal;
    return (
      <div className="h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${over ? 'bg-red-400' : color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    );
  };

  // Always calculate from today's local entries — never from the selected calendar date.
  const today = getCentralDate();
  const todayEntries = (state.foodEntries || []).filter(e => e && e.date === today);
  const todayTotals = todayEntries.reduce(
    (acc, e) => ({
      calories: acc.calories + (parseFloat(e.calories) || 0),
      protein:  acc.protein  + (parseFloat(e.protein)  || 0),
      carbs:    acc.carbs    + (parseFloat(e.carbs)    || 0),
      fat:      acc.fat      + (parseFloat(e.fat)      || 0),
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0 }
  );

  const calories = Math.round(todayTotals.calories);
  const calGoal = state.dailyGoals.calories;
  const calPct = calGoal > 0 ? Math.min(Math.round((calories / calGoal) * 100), 100) : 0;

  return (
    <div
      className={`bg-white dark:bg-gray-900 border-r border-gray-100 dark:border-gray-800 flex-shrink-0 transition-transform duration-300 ${
        isMobile
          ? (isOpen ? 'translate-x-0' : '-translate-x-full')
          : 'translate-x-0'
      } ${isMobile ? 'fixed left-0 top-0 z-30 h-full w-64' : 'h-screen w-64 md:w-64'}`}
    >
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className="flex items-center h-16 px-5 border-b border-gray-100 dark:border-gray-800">
          <img src="/images/ihj-facebook-logo.jpg" alt="Integrative Health Journal" className="h-9 w-9 rounded-full object-cover" />
          <div className="ml-2.5">
            <h1 className="text-base font-bold text-gray-900 dark:text-white leading-tight">IHJ NutriTrack</h1>
            <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Nutrition App</p>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3">
          <p className="px-6 pb-2 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">Menu</p>
          {navItems.map((item, index) => (
            <NavItem key={index} item={item} />
          ))}
        </nav>

        {/* Today's summary */}
        <div className="p-4 border-t border-gray-100 dark:border-gray-800">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-3.5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Today's Progress</h3>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${calPct >= 100 ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' : 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400'}`}>
                {calPct}%
              </span>
            </div>

            <div>
              <div className="flex justify-between items-baseline mb-1">
                <span className="text-[11px] text-gray-500 dark:text-gray-400">Calories</span>
                <span className="text-[11px] font-semibold text-gray-800 dark:text-gray-200">
                  {calories} <span className="text-gray-400 font-normal">/ {calGoal}</span>
                </span>
              </div>
              <ProgressBar value={calories} goal={calGoal} color="bg-indigo-500" />
            </div>

            {[
              { label: 'Protein', val: Math.round(todayTotals.protein), goal: state.dailyGoals.protein, color: 'bg-orange-400', unit: 'g' },
              { label: 'Carbs',   val: Math.round(todayTotals.carbs),   goal: state.dailyGoals.carbs,   color: 'bg-blue-400',   unit: 'g' },
              { label: 'Fat',     val: Math.round(todayTotals.fat),     goal: state.dailyGoals.fat,     color: 'bg-yellow-400', unit: 'g' },
            ].map(m => (
              <div key={m.label}>
                <div className="flex justify-between items-baseline mb-0.5">
                  <span className="text-[11px] text-gray-500 dark:text-gray-400">{m.label}</span>
                  <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300">
                    {m.val}{m.unit} <span className="text-gray-400 font-normal">/ {m.goal}{m.unit}</span>
                  </span>
                </div>
                <ProgressBar value={m.val} goal={m.goal} color={m.color} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
